// src/utils/dates.ts
// Estas funciones YA convertían Timestamps de Firestore en su cuerpo (`'toDate' in date`),
// pero la firma solo admitía Date|string|null → TypeScript reducía esa rama a `never` y la
// daba por muerta, mientras el runtime sí recibe Timestamps (la app viene de Firestore).
// Se amplía la firma con el TimestampLike que ya existe en el proyecto: la rama pasa a ser
// legítima y las llamadas del hook useDateFormatting dejan de chocar. Sin cambio de conducta.
import type { TimestampLike } from '../types/dateLike';

/**
 * Mexico timezone definitions
 */
export const MEXICO_TIMEZONES = [
  { id: 'mexico-pacific', name: 'Zona Pacífico (UTC-7)', timeZone: 'America/Tijuana' },
  { id: 'mexico-mountain', name: 'Zona Montaña (UTC-6)', timeZone: 'America/Chihuahua' },
  { id: 'mexico-central', name: 'Zona Centro (UTC-6)', timeZone: 'America/Mexico_City' },
  { id: 'mexico-southeast', name: 'Zona Sureste (UTC-5)', timeZone: 'America/Cancun' }
];

/**
 * US timezone definitions
 */
export const US_TIMEZONES = [
  { id: 'us-eastern', name: 'Eastern Time (ET)', timeZone: 'America/New_York' },
  { id: 'us-central', name: 'Central Time (CT)', timeZone: 'America/Chicago' },
  { id: 'us-mountain', name: 'Mountain Time (MT)', timeZone: 'America/Denver' },
  { id: 'us-pacific', name: 'Pacific Time (PT)', timeZone: 'America/Los_Angeles' },
  { id: 'us-alaska', name: 'Alaska Time (AKT)', timeZone: 'America/Anchorage' },
  { id: 'us-hawaii', name: 'Hawaii Time (HT)', timeZone: 'Pacific/Honolulu' }
];

/**
 * Canada timezone definitions
 */
export const CANADA_TIMEZONES = [
  { id: 'canada-newfoundland', name: 'Newfoundland Time (NT)', timeZone: 'America/St_Johns' },
  { id: 'canada-atlantic', name: 'Atlantic Time (AT)', timeZone: 'America/Halifax' },
  { id: 'canada-eastern', name: 'Eastern Time (ET)', timeZone: 'America/Toronto' },
  { id: 'canada-central', name: 'Central Time (CT)', timeZone: 'America/Winnipeg' },
  { id: 'canada-mountain', name: 'Mountain Time (MT)', timeZone: 'America/Edmonton' },
  { id: 'canada-pacific', name: 'Pacific Time (PT)', timeZone: 'America/Vancouver' }
];

/**
 * Combined timezones from all regions
 */
export const TIMEZONES = [
  ...MEXICO_TIMEZONES,
  ...US_TIMEZONES,
  ...CANADA_TIMEZONES
];

/**
 * Detects the user's browser timezone
 * @returns The detected timezone ID from the defined timezones or default timezone if not found
 */
export const detectBrowserTimeZone = (): string => {
  try {
    // Get the user's timezone using Intl API
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Try to find a matching timezone in our defined timezones
    const matchedTimezone = TIMEZONES.find(tz => tz.timeZone === userTimeZone);
    
    if (matchedTimezone) {
      return matchedTimezone.id;
    }
    
    // If no direct match, try to determine the region based on the timezone
    if (userTimeZone.includes('America')) {
      if (userTimeZone.includes('Mexico')) {
        return 'mexico-central'; // Default to Mexico Central
      } else if (userTimeZone.includes('Toronto') || userTimeZone.includes('New_York')) {
        return 'us-eastern'; // Default to US Eastern
      } else if (userTimeZone.includes('Vancouver') || userTimeZone.includes('Los_Angeles')) {
        return 'us-pacific'; // Default to US Pacific
      }
    }
    
    // Default fallback
    return 'mexico-central';
  } catch (error) {
    console.error('Error detecting browser timezone:', error);
    return 'mexico-central'; // Safe default
  }
};

/**
 * Formats a date in a standardized string format DD/MM/YYYY
 * 
 * @param date The date to format
 * @returns Formatted date string or 'N/A' if invalid
 */
export const formatDate = (date: Date | null | string | TimestampLike): string => {
  if (!date) return 'N/A';
  try {
    let dateObj: Date;
    if (date instanceof Date) { 
      dateObj = date; 
    } else if (typeof date === 'string') { 
      dateObj = new Date(date); 
    } else if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') { 
      dateObj = (date as any).toDate(); 
    } else { 
      return 'Fecha inválida'; 
    }
    
    if (isNaN(dateObj.getTime())) return 'Fecha inválida';
    return new Intl.DateTimeFormat('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    }).format(dateObj);
  } catch(e) { 
    console.error("Error formatting date:", e, date); 
    return 'Fecha inválida'; 
  }
};

/**
 * Format birthday from day and month
 * @param day Day of birthday (1-31)
 * @param month Month of birthday (0-11)
 * @returns Formatted string like "12 de Enero" or "N/A" if invalid
 */
// ⚠️ month es 0-11 (índice JS, convención de birthdaysApi). clientsApi entrega birthMonth 1-12: restar 1 al llamar.
export const formatBirthday = (day: number | null, month: number | null): string => {
  if (day === null || month === null) return 'N/A';
  
  try {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    
    // Validate day and month
    if (month < 0 || month > 11 || day < 1 || day > 31) return 'Fecha inválida';
    
    // Additional validation for months with less than 31 days
    if ((month === 3 || month === 5 || month === 8 || month === 10) && day > 30) return 'Fecha inválida';
    if (month === 1) { // February
      const isLeapYear = new Date().getFullYear() % 4 === 0;
      if ((isLeapYear && day > 29) || (!isLeapYear && day > 28)) return 'Fecha inválida';
    }
    
    return `${day} de ${months[month]}`;
  } catch (e) {
    console.error("Error formatting birthday:", e);
    return 'Fecha inválida';
  }
};

/**
 * Format a date string using the business timezone
 * @param date The date to format
 * @param businessTypeId The business type ID to get the timezone
 * @returns Formatted date string
 */
export const formatDateInBusinessTZ = (date: Date | string | TimestampLike | null | undefined, timezone?: string): string => {
  if (!date) return 'N/A';
  
  try {
    let dateObj: Date;
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
      dateObj = (date as any).toDate();
    } else {
      return 'Fecha inválida';
    }
    
    if (isNaN(dateObj.getTime())) return 'Fecha inválida';
    
    // Get timezone from business type if provided
    let tzOptions = {};
    if (timezone) {
      tzOptions = { timeZone: timezone };
    }
    
    return new Intl.DateTimeFormat('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...tzOptions
    }).format(dateObj);
  } catch(e) {
    console.error("Error formatting date with business TZ:", e, date);
    return 'Fecha inválida';
  }
};

/**
 * Format only the date part in a specific timezone
 * @param date The date to format
 * @param timezone The timezone to use
 * @returns Formatted date string (just date part)
 */
export const formatDateOnlyInBusinessTZ = (date: Date | string | TimestampLike | null | undefined, timezone?: string): string => {
  if (!date) return 'N/A';
  
  try {
    let dateObj: Date;
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
      dateObj = (date as any).toDate();
    } else {
      return 'Fecha inválida';
    }
    
    if (isNaN(dateObj.getTime())) return 'Fecha inválida';
    
    let tzOptions = {};
    if (timezone) {
      tzOptions = { timeZone: timezone };
    }
    
    return new Intl.DateTimeFormat('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      ...tzOptions
    }).format(dateObj);
  } catch(e) {
    console.error("Error formatting date-only with TZ:", e, date);
    return 'Fecha inválida';
  }
};

/**
 * Format only the time part in a specific timezone
 * @param date The date to format
 * @param timezone The timezone to use
 * @returns Formatted time string
 */
export const formatTimeOnlyInBusinessTZ = (date: Date | string | TimestampLike | null | undefined, timezone?: string): string => {
  if (!date) return 'N/A';
  
  try {
    let dateObj: Date;
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
      dateObj = (date as any).toDate();
    } else {
      return 'Hora inválida';
    }
    
    if (isNaN(dateObj.getTime())) return 'Hora inválida';
    
    let tzOptions = {};
    if (timezone) {
      tzOptions = { timeZone: timezone };
    }
    
    return new Intl.DateTimeFormat('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit',
      ...tzOptions
    }).format(dateObj);
  } catch(e) {
    console.error("Error formatting time-only with TZ:", e, date);
    return 'Hora inválida';
  }
};

/**
 * Format a date without timezone correction
 * @param date The date to format
 * @param options Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export const formatDateWithoutTZ = (
  date: Date | string | TimestampLike | null | undefined, 
  options: Intl.DateTimeFormatOptions = { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric' 
  }
): string => {
  if (!date) return 'N/A';
  
  try {
    let dateObj: Date;
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
      dateObj = (date as any).toDate();
    } else {
      return 'Fecha inválida';
    }
    
    if (isNaN(dateObj.getTime())) return 'Fecha inválida';
    
    return new Intl.DateTimeFormat('es-ES', options).format(dateObj);
  } catch(e) {
    console.error("Error formatting date without TZ:", e, date);
    return 'Fecha inválida';
  }
};

/**
 * Calculate relative time (e.g., "5 minutes ago")
 * @param date The date to calculate time ago from
 * @returns Formatted time ago string
 */
export const timeAgo = (date: Date | null | undefined): string => {
  if (!date) return 'N/A';
  
  try {
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    // Define the time intervals in seconds
    const intervals = {
      año: 31536000,
      mes: 2592000,
      semana: 604800,
      día: 86400,
      hora: 3600,
      minuto: 60
    };
    
    // Function to get correct plural form
    const getPlural = (count: number, unit: string): string => {
      if (count === 1) return `${count} ${unit}`;
      if (unit === 'mes') return `${count} meses`;
      return `${count} ${unit}s`;
    };
    
    // Check each interval
    if (seconds < 60) return 'justo ahora';
    
    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const count = Math.floor(seconds / secondsInUnit);
      if (count > 0) {
        return `hace ${getPlural(count, unit)}`;
      }
    }
    
    return 'justo ahora';
  } catch(e) {
    console.error("Error calculating time ago:", e, date);
    return 'Fecha inválida';
  }
};

/**
 * Convert a date between two timezones
 * @param date The date to convert
 * @param fromTimeZone The source timezone
 * @param toTimeZone The target timezone
 * @returns Converted date object
 */
export const convertDateBetweenTimeZones = (
  date: Date | null | undefined | any,
  fromTimeZone: string,
  toTimeZone: string
): Date | null => {
  if (!date) return null;
  
  try {
    // Convert any Timestamp or custom date object to Date
    let dateObj: Date;
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } else {
      return null;
    }
    
    if (isNaN(dateObj.getTime())) return null;
    
    // If timezones are the same, no conversion needed
    if (fromTimeZone === toTimeZone) return new Date(dateObj);
    
    // Get offset difference between the two timezones
    const offsetDiff = getTimeZoneOffsetDiff(fromTimeZone, toTimeZone);
    
    // Apply the offset difference to create a new date
    const newDate = new Date(dateObj.getTime() + offsetDiff * 60 * 1000);
    
    return newDate;
  } catch(e) {
    console.error("Error converting between timezones:", e, date, fromTimeZone, toTimeZone);
    return null;
  }
};

/**
 * Get the offset difference in minutes between two timezones
 * @param timezone1 First timezone
 * @param timezone2 Second timezone
 * @returns Difference in minutes (timezone1 - timezone2)
 */
export const getTimeZoneOffsetDiff = (timezone1: string, timezone2: string): number => {
  try {
    // Use a specific date for consistency
    const date = new Date();
    
    // Get timezone offsets in minutes
    const tz1Parts = timezone1.split('/');
    const tz2Parts = timezone2.split('/');
    
    const tz1 = TIMEZONES.find(tz => 
      tz.id === timezone1 || (tz1Parts.length > 1 && tz.timeZone === timezone1));
    
    const tz2 = TIMEZONES.find(tz => 
      tz.id === timezone2 || (tz2Parts.length > 1 && tz.timeZone === timezone2));
    
    // If we found the timezone, get the actual IANA timezone string
    const actualTZ1 = (tz1?.timeZone || timezone1);
    const actualTZ2 = (tz2?.timeZone || timezone2);
    
    // Calculate string representations in each timezone
    const str1 = date.toLocaleString('en-US', { timeZone: actualTZ1 });
    const str2 = date.toLocaleString('en-US', { timeZone: actualTZ2 });
    
    // Parse the string representations back to Date objects
    const date1 = new Date(str1);
    const date2 = new Date(str2);
    
    // Calculate the difference in minutes
    return (date2.getTime() - date1.getTime()) / (60 * 1000);
  } catch(e) {
    console.error("Error calculating timezone offset difference:", e, timezone1, timezone2);
    return 0;
  }
};

/**
 * Get a human-readable difference text between two timezones
 * @param timezone1 First timezone
 * @param timezone2 Second timezone
 * @returns Human-readable difference text
 */
export const getTimeZoneDifferenceText = (timezone1: string, timezone2: string): string => {
  try {
    const diff = getTimeZoneOffsetDiff(timezone1, timezone2);
    
    if (diff === 0) return 'misma zona horaria';
    
    const hours = Math.abs(Math.floor(diff / 60));
    const minutes = Math.abs(diff % 60);
    
    let result = '';
    
    if (hours > 0) {
      result += `${hours} hora${hours > 1 ? 's' : ''}`;
    }
    
    if (minutes > 0) {
      if (hours > 0) result += ' y ';
      result += `${minutes} minuto${minutes > 1 ? 's' : ''}`;
    }
    
    if (diff > 0) {
      return `${result} adelante`;
    } else {
      return `${result} atrás`;
    }
  } catch(e) {
    console.error("Error creating timezone difference text:", e, timezone1, timezone2);
    return '';
  }
};

/**
 * Get a display label for a timezone
 * @param timezone Timezone ID or IANA string
 * @returns Human-readable timezone label
 */
export const getTimeZoneLabel = (timezone: string): string => {
  try {
    // First check if it's one of our defined timezones
    const tzParts = timezone.split('/');
    const definedTZ = TIMEZONES.find(tz => 
      tz.id === timezone || (tzParts.length > 1 && tz.timeZone === timezone));
    
    if (definedTZ) {
      return definedTZ.name;
    }
    
    // If not found, generate a label from the IANA timezone string
    if (tzParts.length > 1) {
      const city = tzParts[tzParts.length - 1].replace(/_/g, ' ');
      
      // Get UTC offset
      const now = new Date();
      const utcOffset = -now.getTimezoneOffset() / 60;
      const offsetStr = utcOffset >= 0 ? `+${utcOffset}` : `${utcOffset}`;
      
      return `${city} (UTC${offsetStr})`;
    }
    
    return timezone; // Fallback
  } catch(e) {
    console.error("Error getting timezone label:", e, timezone);
    return timezone;
  }
};

/**
 * Check if two dates are the same day in a specific timezone
 * @param date1 First date
 * @param date2 Second date
 * @param timezone Timezone to use for comparison
 * @returns Boolean indicating if dates are the same day
 */
export const isSameDayInTimeZone = (
  date1: Date | null | undefined | any,
  date2: Date | null | undefined | any,
  timezone: string
): boolean => {
  if (!date1 || !date2) return false;
  
  try {
    // Convert to Date objects
    let dateObj1: Date;
    let dateObj2: Date;
    
    if (date1 instanceof Date) {
      dateObj1 = date1;
    } else if (typeof date1 === 'string') {
      dateObj1 = new Date(date1);
    } else if (date1 && typeof date1 === 'object' && 'toDate' in date1 && typeof date1.toDate === 'function') {
      dateObj1 = date1.toDate();
    } else {
      return false;
    }
    
    if (date2 instanceof Date) {
      dateObj2 = date2;
    } else if (typeof date2 === 'string') {
      dateObj2 = new Date(date2);
    } else if (date2 && typeof date2 === 'object' && 'toDate' in date2 && typeof date2.toDate === 'function') {
      dateObj2 = date2.toDate();
    } else {
      return false;
    }
    
    if (isNaN(dateObj1.getTime()) || isNaN(dateObj2.getTime())) return false;
    
    // Get timezone string from our defined timezones if needed
    const tzParts = timezone.split('/');
    const definedTZ = TIMEZONES.find(tz => 
      tz.id === timezone || (tzParts.length > 1 && tz.timeZone === timezone));
    
    const actualTZ = (definedTZ?.timeZone || timezone);
    
    // Format both dates to strings in the specified timezone, keeping only the date part
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'numeric', 
      day: 'numeric',
      timeZone: actualTZ
    };
    
    const formatter = new Intl.DateTimeFormat('en-US', options);
    
    const dateStr1 = formatter.format(dateObj1);
    const dateStr2 = formatter.format(dateObj2);
    
    return dateStr1 === dateStr2;
  } catch(e) {
    console.error("Error comparing dates in timezone:", e, date1, date2, timezone);
    return false;
  }
};